<template>
  <b-button
    variant="success"
    class="btn__start-online-meeting"
    :class="btnClasses"
    :disabled="!isCanJoinMeeting"
    @click="startOnlineMeeting"
  >
    <b-spinner v-if="isChecking" label="Checking meeting" />

    <span v-else>{{ isOwnerOrInstructor ? 'Start Meeting' : 'Join Meeting' }}</span>
  </b-button>
</template>

<script>
import moment from 'moment';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import LearningUnit from '@/models/LearningUnit';
import Meeting from '@/models/Meeting';

const JBH_TIME = 10; // join before host time (minutes)
const MEETING_DURATION = 45;

export default {
  name: 'BaseStartMeetingButton',

  mixins: [datetimeMixin, userInfoMixin],

  props: {
    section: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      isChecking: false,
      meeting: null,
      checkMeetingInterval: null,
      checkStartTime: null,
      checkEndTime: null,
      isCanJoinMeeting: false
    };
  },

  computed: {
    btnClasses() {
      return {
        active: this.isOnStartTime()
      };
    },

    learningUnit() {
      return this.section ? LearningUnit.find(this.section.learning_unit_id) : null;
    },

    isOwnerOrInstructor() {
      return this.learningUnit.isOwnerOrInstructor(this.user.id);
    },

    sectionStartTime() {
      return this.formatedComparableDatetime(this.section.start_at);
    },

    meetingStartTime() {
      return this.meeting ? this.formatedComparableDatetime(this.meeting.start_time) : null;
    },

    isOnSectionStartTime() {
      const currentDatetime = this.formatedComparableToday();

      return moment(new Date(currentDatetime)).isSameOrAfter(new Date(this.sectionStartTime));
    }
  },

  mounted() {
    this.getNextMeetingBySection();

    this.checkMeetingTime();

    this.checkStartTime = setInterval(this.checkMeetingTime, 1000 * 5);
  },

  beforeDestroy() {
    clearInterval(this.checkMeetingInterval);

    this.checkMeetingInterval = null;
  },

  methods: {
    // can join before meeting time 10 minute
    checkMeetingTime() {
      if (this.meeting) {
        const today = moment();

        const meetingDatetime = moment(this.meeting.start_time);

        const minDiff = meetingDatetime.diff(today, 'minutes');

        if (minDiff <= JBH_TIME) {
          clearInterval(this.checkStartTime);

          const isMeetingEnd = this.checkMeetingEndTime();

          if (!isMeetingEnd) {
            this.checkEndTime = setInterval(this.checkMeetingEndTime, 1000 * 5);
            this.isCanJoinMeeting = true;
          }
        }
      }
    },

    checkMeetingEndTime() {
      const today = moment();
      const meetingDatetime = moment(this.meeting.start_time);
      const minDiff = today.diff(meetingDatetime, 'minutes');
      const isMeetingEnd = minDiff > MEETING_DURATION;

      if (isMeetingEnd) {
        clearInterval(this.checkEndTime);
        this.isCanJoinMeeting = false;
      }

      return isMeetingEnd;
    },

    isOnMeetingStartTime() {
      const currentDatetime = this.formatedComparableToday();

      return moment(new Date(currentDatetime)).isSameOrAfter(new Date(this.meetingStartTime));
    },

    isOnStartTime() {
      if (!this.section?.start_at || !this.meeting?.start_time) return false;

      return this.isOnSectionStartTime && this.isOnMeetingStartTime();
    },

    getNextMeetingBySection() {
      if (!this.section?.id) return;

      let promise;

      this.isChecking = true;

      try {
        const meetings = Meeting.query().where('section_id', this.section.id).get();

        if (meetings.length) {
          const sortedMeetings = this.sortDateCollection(meetings, 'start_time');

          const today = moment(); // local by default
          for (let index = 0; index < sortedMeetings.length; index++) {
            const meetingDateTime = moment(sortedMeetings[index].start_time);

            const minDiff = meetingDateTime.diff(today, 'minutes');

            if (minDiff > -MEETING_DURATION) {
              this.meeting = sortedMeetings[index];

              break;
            }
          }
        } else return;
      } catch (error) {
        console.error(error);
      } finally {
        this.isChecking = false;
      }

      return promise;
    },

    startOnlineMeeting() {
      if (this.meeting) {
        let url = this.meeting.join_url;

        location.assign(url);
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
